import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  AlertTitle
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';

interface DeleteConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  filename: string;
  fileSize?: number;
  fileType?: string;
}

export default function DeleteConfirmationDialog({
  open,
  onClose,
  onConfirm,
  filename,
  fileSize,
  fileType
}: DeleteConfirmationDialogProps) {
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <DeleteIcon color="error" />
          <Typography variant="h6">Delete File?</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>This action cannot be undone</AlertTitle>
          Are you sure you want to delete this file?
        </Alert>

        <Box 
          sx={{ 
            p: 2, 
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            backgroundColor: 'background.paper',
            mb: 2
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            📄 {filename}
          </Typography>
          <Box display="flex" gap={2} sx={{ color: 'text.secondary' }}>
            {fileSize && (
              <Typography variant="body2">
                Size: {formatFileSize(fileSize)}
              </Typography>
            )}
            {fileType && (
              <Typography variant="body2">
                Type: {fileType}
              </Typography>
            )}
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={1} sx={{ color: 'text.secondary' }}>
          <WarningIcon fontSize="small" />
          <Typography variant="body2">
            The file will be removed from your case but can be recovered by system administrators if needed.
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={onConfirm} 
          color="error" 
          variant="contained"
          startIcon={<DeleteIcon />}
        >
          Delete File
        </Button>
      </DialogActions>
    </Dialog>
  );
}